import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import styled from 'styled-components';

import {
  ListRegistrationItem,
  ListEventInfo,
  ListStudentName,
  ListEventMeta,
  ListRemoveLink,
  ListEmptyState,
} from './EventRegistrationStyles';

const Header1 = styled.div`
  font-size: 18px;
  font-weight: bold;
  color: #479de6;
`;

const ViewAllLink = styled(Link)`
  display: inline-block;
  color: #479de6;
  font-size: 13px;
  margin-top: 8px;

  &:hover {
    text-decoration: underline;
  }
`;

const MyRegistrations = ({ compact }) => {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchRegistrations = async () => {
      try {
        const response = await axios.get('/api/v2/event-registrations');
        setRegistrations(response.data.registrations || []);
      } catch (err) {
        setError('Failed to load registrations');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchRegistrations();
  }, []);

  const handleRemove = async (id) => {
    if (!window.confirm('Remove this registration?')) return;

    try {
      await axios.delete(`/api/v2/event-registrations/${id}`);
      setRegistrations((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      console.error('Failed to remove registration:', err);
    }
  };

  if (loading) return <p>Loading registrations...</p>;
  if (error) return <p className="text-danger">{error}</p>;

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = registrations.filter(
    (r) => !r.eventDate || r.eventDate >= today,
  );

  if (compact) {
    if (upcoming.length === 0) return null;

    return (
      <div>
        <div
          style={{
            fontSize: '13px',
            fontWeight: '600',
            color: '#5f6368',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '8px',
          }}
        >
          Upcoming Registrations
        </div>
        {upcoming.map((reg) => {
          const dateDisplay = reg.eventDate
            ? new Date(reg.eventDate + 'T00:00').toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
              })
            : null;
          return (
            <div
              key={reg.id}
              style={{ fontSize: '13px', color: '#5f6368', padding: '3px 0' }}
            >
              {reg.studentFirstName} {reg.studentLastName} — {reg.eventName}
              {dateDisplay ? ` (${dateDisplay})` : ''}
            </div>
          );
        })}
        <Link
          to="/my-account"
          style={{
            fontSize: '13px',
            color: '#479de6',
            marginTop: '4px',
            display: 'inline-block',
          }}
        >
          Manage registrations
        </Link>
      </div>
    );
  }

  const renderItem = (reg) => {
    const dateDisplay = reg.eventDate
      ? new Date(reg.eventDate + 'T00:00').toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })
      : null;

    return (
      <ListRegistrationItem key={reg.id}>
        <ListEventInfo>
          <ListStudentName>
            {reg.studentFirstName} {reg.studentLastName}
          </ListStudentName>
          <ListEventMeta>{reg.eventName}</ListEventMeta>
          {(reg.venueName || dateDisplay) && (
            <ListEventMeta>
              {reg.venueName}
              {reg.venueName && dateDisplay ? ' | ' : ''}
              {dateDisplay}
            </ListEventMeta>
          )}
        </ListEventInfo>
        <ListRemoveLink onClick={() => handleRemove(reg.id)}>
          Remove
        </ListRemoveLink>
      </ListRegistrationItem>
    );
  };

  return (
    <div>
      <Header1>Event Registrations</Header1>
      <hr />
      {upcoming.length === 0 ? (
        <ListEmptyState>No upcoming event registrations.</ListEmptyState>
      ) : (
        upcoming.map(renderItem)
      )}
      {registrations.length > upcoming.length && (
        <ViewAllLink to="/my-account/registrations">
          View all registrations ({registrations.length})
        </ViewAllLink>
      )}
    </div>
  );
};

export default MyRegistrations;
